import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type {
  CreditParameterCatalog,
  CreditParameterDiffEntry,
} from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonText } from '@/shared/ui/Skeleton';

import { mensagemDoErro, rotaAusente, type EscopoDoEditor } from './escopo';
import { ValorLegivel } from './ValorLegivel';
import { compararLocalmente } from './valores';

/**
 * Duas versoes comparadas parametro a parametro.
 *
 * Usa a comparacao do servidor. Onde a rota nao existe (o padrao global antes da
 * governanca), le as duas versoes e compara na tela, pela mesma regra.
 */
export function ComparacaoDeVersoes({
  escopo,
  catalogo,
  rotulos,
  baseId,
  outraId,
}: {
  escopo: EscopoDoEditor;
  catalogo: CreditParameterCatalog;
  rotulos: Record<string, string>;
  baseId: string;
  outraId: string;
}) {
  const [soAlterados, setSoAlterados] = useState(true);
  const consulta = useQuery({
    queryKey: [escopo.chave, 'comparacao', baseId, outraId],
    queryFn: async ({ signal }) => {
      try {
        return await escopo.api.diff(baseId, outraId, signal);
      } catch (erro) {
        if (!rotaAusente(erro)) throw erro;
        const [base, outra] = await Promise.all([
          escopo.api.get(baseId, signal),
          escopo.api.get(outraId, signal),
        ]);
        return compararLocalmente(catalogo, base, outra);
      }
    },
  });

  if (consulta.isPending) return <SkeletonText lines={4} />;
  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel comparar as versoes">
        {mensagemDoErro(consulta.error)}
      </Alert>
    );
  }

  const comparacao = consulta.data;
  const linhas = soAlterados
    ? comparacao.parameters.filter((p) => p.changed)
    : comparacao.parameters;
  const nomeA = `Versao ${comparacao.base.version}`;
  const nomeB = `Versao ${comparacao.other.version}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-700">
          {comparacao.changed_count === 0
            ? `${nomeA} e ${nomeB} tem os mesmos valores.`
            : `${comparacao.changed_count} parametro${comparacao.changed_count === 1 ? '' : 's'} diferente${comparacao.changed_count === 1 ? '' : 's'} entre a ${nomeA.toLowerCase()} e a ${nomeB.toLowerCase()}.`}
        </p>
        <label className="flex items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={soAlterados}
            onChange={(event) => setSoAlterados(event.target.checked)}
          />
          Mostrar so os alterados
        </label>
      </div>
      <DataTable<CreditParameterDiffEntry>
        columns={[
          {
            key: 'parametro',
            header: 'Parametro',
            render: (linha) => (
              <div className="flex flex-col">
                <span className="font-medium text-ink-900">{linha.label}</span>
                <code className="text-2xs text-ink-500">{linha.key}</code>
              </div>
            ),
          },
          {
            key: 'base',
            header: nomeA,
            render: (linha) => <ValorLegivel valor={linha.base} rotulos={rotulos} />,
          },
          {
            key: 'outra',
            header: nomeB,
            render: (linha) => <ValorLegivel valor={linha.other} rotulos={rotulos} />,
          },
        ]}
        items={linhas}
        keyOf={(linha) => linha.key}
        empty={<EmptyState icon="check" title="Nenhum parametro diferente" />}
      />
    </div>
  );
}
